import type { TTicketDuration } from "utils/durationsOptions";

import { List, Record } from "immutable";
import { getMaintainerModels } from "utils/maintainers";

import { IntegrationActorPermissionModel } from "./IntegrationActorPermissionModel";
import { IntegrationModel } from "./IntegrationModel";
import { IntegrationResourceRoleModel } from "./IntegrationResourceRoleModel";

const defaults = {
  allowedDurations: null as List<TTicketDuration> | null,
  approvalAlgorithmId: null as null | string,
  createdAt: null as Date | null,
  description: null as null | string,
  eligible: false,
  entitlements: null as List<IntegrationActorPermissionModel> | null,
  euid: "",
  givesAccessToRoles: null as List<IntegrationResourceRoleModel> | null,
  hasOnlyUnmanagedRoles: false,
  id: "",
  integration: null as IntegrationModel | null,
  integrationId: "",
  integrationResourceRoles: null as List<IntegrationResourceRoleModel> | null,
  isDeleted: false,
  maintainerGroupId: null as null | string,
  maintainers: null as List<TMaintainerModel> | null,
  multirole: false,
  name: "",
  ownerId: null as null | string,
  prerequisitePermissionId: null as null | string,
  requestable: false,
  rolesCount: null as null | number,
  tags: null as null | string[],
  type: "",
  userDefinedDescription: null as null | string,
  userDefinedTags: null as null | string[],
};

export type TIntegrationResourceModel = typeof defaults;

export class IntegrationResourceModel extends Record<TIntegrationResourceModel>(defaults) {
  get calculatedDescription() {
    return this.userDefinedDescription ?? this.description;
  }

  get calculatedTags() {
    return (this.tags || []).concat(this.userDefinedTags || []);
  }

  static fromServerData(data: unknown) {
    const {
      allowedDurations,
      approvalAlgorithmId,
      createdAt,
      description,
      eligible,
      entitlements,
      euid,
      givesAccessToRoles,
      hasOnlyUnmanagedRoles,
      id,
      integration,
      integrationId,
      integrationResourceRoles,
      isDeleted,
      maintainerGroupId,
      maintainers,
      multirole,
      name,
      ownerId,
      prerequisitePermissionId,
      requestable,
      rolesCount,
      tags,
      type,
      userDefinedDescription,
      userDefinedTags,
    } = data as typeof defaults;

    return new IntegrationResourceModel({
      allowedDurations: allowedDurations ? List(allowedDurations) : null,
      approvalAlgorithmId,
      createdAt: createdAt ? new Date(createdAt as unknown as string) : null,
      description,
      eligible,
      entitlements: entitlements ? List(entitlements.map(IntegrationActorPermissionModel.fromServerData)) : null,
      euid,
      givesAccessToRoles: givesAccessToRoles
        ? List(givesAccessToRoles.map(IntegrationResourceRoleModel.fromServerData))
        : null,
      hasOnlyUnmanagedRoles,
      id,
      integration: integration ? IntegrationModel.fromServerData(integration) : null,
      integrationId,
      integrationResourceRoles: integrationResourceRoles
        ? List(integrationResourceRoles.map(IntegrationResourceRoleModel.fromServerData))
        : null,
      isDeleted,
      maintainerGroupId,
      maintainers: getMaintainerModels(maintainers),
      multirole,
      name,
      ownerId,
      prerequisitePermissionId,
      requestable: requestable,
      rolesCount: typeof rolesCount === "number" ? Number(rolesCount) : null,
      tags,
      type,
      userDefinedDescription,
      userDefinedTags,
    });
  }
}
